/**
 * Agent Amendment History Card
 *
 * Agent-facing view of contract amendments in the Wallet page.
 * Uses friendly, trust-building language — no internal system terms.
 */

import { useState, useEffect } from 'react';
import { History, ArrowUpCircle, ArrowDownCircle, DollarSign } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface Amendment {
  id: string;
  amendment_type: string;
  old_total_leads: number;
  new_total_leads: number;
  old_price_per_lead: number;
  new_price_per_lead: number;
  leads_delta: number;
  amount_cents: number;
  created_at: string;
}

interface AgentAmendmentHistoryProps {
  contractIds: string[];
}

const FRIENDLY_LABELS: Record<string, string> = {
  upgrade: 'Plan Upgrade',
  downgrade: 'Plan Adjustment',
  price_change: 'Pricing Update',
  upgrade_and_price_change: 'Plan Upgrade',
  downgrade_and_price_change: 'Plan Adjustment',
};

export function AgentAmendmentHistory({ contractIds }: AgentAmendmentHistoryProps) {
  const [amendments, setAmendments] = useState<Amendment[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (contractIds.length === 0) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    async function fetch() {
      setLoading(true);
      const { data, error } = await supabase
        .from('contract_amendments')
        .select('id, amendment_type, old_total_leads, new_total_leads, old_price_per_lead, new_price_per_lead, leads_delta, amount_cents, created_at')
        .in('contract_id', contractIds)
        .order('created_at', { ascending: false })
        .limit(10);

      if (!cancelled) {
        if (error) {
          console.error('[AgentAmendmentHistory] Error:', error);
        }
        setAmendments(data ?? []);
        setLoading(false);
      }
    }

    fetch();
    return () => { cancelled = true; };
  }, [contractIds.join(',')]);

  if (loading) return null;
  if (amendments.length === 0) return null;

  return (
    <div className="bg-card rounded-2xl border border-border p-5">
      <div className="flex items-center gap-2 mb-4">
        <History className="w-4 h-4 text-muted-foreground" />
        <h3 className="font-display text-base font-semibold text-foreground">Contract Updates</h3>
      </div>
      <div className="space-y-3">
        {amendments.map((a) => {
          const isUpgrade = a.leads_delta > 0;
          const isDowngrade = a.leads_delta < 0;
          const leadsChanged = a.leads_delta !== 0;
          const priceChanged = a.old_price_per_lead !== a.new_price_per_lead;
          const label = FRIENDLY_LABELS[a.amendment_type] ?? 'Contract Update';
          const date = new Date(a.created_at).toLocaleDateString('en-US', {
            month: 'short', day: 'numeric', year: 'numeric',
          });

          return (
            <div key={a.id} className="p-3 rounded-xl border border-border bg-muted/20 space-y-1.5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {isUpgrade && <ArrowUpCircle className="w-4 h-4 text-green-600" />}
                  {isDowngrade && <ArrowDownCircle className="w-4 h-4 text-orange-600" />}
                  {!leadsChanged && <DollarSign className="w-4 h-4 text-blue-600" />}
                  <span className="text-sm font-medium">{label}</span>
                </div>
                <span className="text-xs text-muted-foreground">{date}</span>
              </div>

              <div className="text-xs text-muted-foreground space-y-0.5">
                {leadsChanged && (
                  <p>
                    Lead target updated from {a.old_total_leads} to {a.new_total_leads} leads
                    {isUpgrade ? ` (+${a.leads_delta})` : ` (${a.leads_delta})`}
                  </p>
                )}
                {priceChanged && (
                  <p>
                    Price per lead updated from ${a.old_price_per_lead.toFixed(2)} to ${a.new_price_per_lead.toFixed(2)}
                  </p>
                )}
                {a.amount_cents > 0 && (
                  <p className="text-foreground font-medium">
                    ${(a.amount_cents / 100).toFixed(2)} will be added to your next invoice
                  </p>
                )}
                {a.amount_cents < 0 && (
                  <p className="text-green-700 font-medium">
                    A credit of ${(Math.abs(a.amount_cents) / 100).toFixed(2)} has been applied to your next invoice
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
